import * as z from 'zod/mini'
import { endAfterStart, isoDateTime, queryNumber } from '../../schemas/common'
import { reservationFields, reservationTextFields } from '../../schemas/reservation'
import type { BasicInfo, ManagedReservation } from '../../types/interfaces'
import type { ParticipationStatus } from '../../utils/event-participation-status'
import type { ReservationState } from '../../utils/reservation-state'

/**
 * A reservation a manager books for the premise. The ids are only shape-checked here; that they
 * belong to the premise is checked in `ReservationService.createEntityReservation`
 * (`requireEntityX`, 404) - see "Access, tenancy and rules" in docs/validation.md.
 */
export const createManagedReservationBody = z
  .object({
    ...reservationFields,
    startDateTime: z.iso.datetime({ offset: true }),
    endDateTime: z.iso.datetime({ offset: true }),
    activityId: z.optional(z.uuid()),
    placeId: z.optional(z.uuid()),
    customerId: z.optional(z.uuid()),
    employeeIds: z.array(z.uuid()),
    description: z.optional(reservationTextFields.description),
    /** E-mail the customer a confirmation with the reservation's link. Off unless asked. */
    notifyCustomer: z.optional(z.boolean()),
  })
  .check(z.superRefine(endAfterStart('startDateTime', 'endDateTime')))
export type CreateManagedReservationBody = z.infer<typeof createManagedReservationBody>

/**
 * Everything the premise may change on a reservation, sent whole. Refused once the reservation has started or was
 * cancelled (`reservation.started`, `reservation.cancelled`). The customer is not editable: another customer is a new
 * reservation. A changed time, place or employee is checked for availability (`reservation.slotUnavailable`).
 */
export const editReservationBody = z
  .object({
    name: reservationFields.name,
    startDateTime: z.iso.datetime({ offset: true }),
    endDateTime: z.iso.datetime({ offset: true }),
    placeId: z.nullable(z.uuid()),
    activityId: z.nullable(z.uuid()),
    employeeIds: z.array(z.uuid()),
    /** `''` removes it. */
    description: reservationTextFields.description,
    /** E-mail the customer the change, with a fresh link. Off unless asked. */
    notifyCustomer: z.optional(z.boolean()),
  })
  .check(z.superRefine(endAfterStart('startDateTime', 'endDateTime')))
export type EditReservationBody = z.infer<typeof editReservationBody>

/** Cancelling keeps the reservation, marked cancelled. Refused once it started (`reservation.started`). */
export const cancelReservationBody = z.object({
  notifyCustomer: z.boolean(),
  /** Goes into the cancel e-mail. */
  reason: z.optional(reservationTextFields.cancellationReason),
})
export type CancelReservationBody = z.infer<typeof cancelReservationBody>

/** The internal note (pilot). Allowed at any time; `''` removes it. */
export const editReservationNoteBody = z.object({ note: reservationTextFields.note })
export type EditReservationNoteBody = z.infer<typeof editReservationNoteBody>

/** Reservations overlapping `[start, end)`; both bounds optional. */
export const getManagedReservationsQuery = z.object({
  start: z.optional(isoDateTime),
  end: z.optional(isoDateTime),
})
export type GetManagedReservationsQuery = z.infer<typeof getManagedReservationsQuery>

/** A reservation URL (`.../reservations/:reservationId`), looked up by `ReservationGuard` within the premise. */
export const reservationParams = z.object({ reservationId: z.uuid() })
export type ReservationParams = z.infer<typeof reservationParams>

/** The customer a reservation is for, with what the premise knows about them. */
export interface ManagedReservationCustomer {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  /** Whole years from the client row's birth date; null when unknown. */
  age: number | null
  /** The premise's own note on the customer (`CustomerEntity.description`, "Poznámka ku klientovi"). */
  description: string
  marketingConsent: boolean
  blocked: boolean
  createdAt: Date
}

/** One reservation on its own page. */
export interface ManagedReservationDetail extends ManagedReservation {
  /** HTML, the customer's. */
  description: string
  /** The internal note (pilot). */
  note: string | null
  cancellationDeadline: number
  cancellationReason: string | null
  /** Who cancelled it: the customer through their link, or a member of staff (by name). */
  cancelledBy: { kind: 'customer' | 'staff'; name: string } | null
  /** The service it was booked from; null for a manual reservation. */
  service: BasicInfo | null
  source: 'public' | 'manual'
  customer: ManagedReservationCustomer | null
}

/**
 * Whether a client came to an event: set only once a confirmed sign-up's event has ended. Attendance is assumed, so
 * `noShow` is a sign-up staff marked "Neprišiel" (docs/events.md).
 */
export type CustomerAttendance = 'attended' | 'noShow'

/** One entry of a customer's schedule at the premise: a reservation or an event sign-up. */
export type CustomerScheduleItem =
  | (CustomerScheduleBase & { kind: 'reservation'; state: ReservationState })
  | (CustomerScheduleBase & { kind: 'event'; status: ParticipationStatus; attendance: CustomerAttendance | null })

interface CustomerScheduleBase {
  id: string
  name: string
  start: Date
  end: Date
  place: string | null
  employees: string[]
}

/** How many entries either side of now the schedule lists unless asked for more; the counts cover everything. */
export const CUSTOMER_SCHEDULE_LIMIT = 10

/** The most past entries one request lists. The history is read whole either way; this only bounds the response. */
export const CUSTOMER_SCHEDULE_MAX_PAST = 500

/** `?pastLimit=`: how many past entries to list ("Načítať staršie" asks for more). */
export const getCustomerScheduleQuery = z.object({
  pastLimit: z.optional(queryNumber(z.int().check(z.minimum(1), z.maximum(CUSTOMER_SCHEDULE_MAX_PAST)))),
})
export type GetCustomerScheduleQuery = z.infer<typeof getCustomerScheduleQuery>

/** `GET manage/entities/:entityId/customers/:customerId/schedule`. */
export interface CustomerSchedule {
  counts: {
    reservations: number
    completed: number
    cancelled: number
    events: number
    /** Confirmed sign-ups to ended events the client did not come to. Events only: reservations have no attendance. */
    noShows: number
    /** Every entry that has not started, and every one that has, reservations and sign-ups together. */
    upcoming: number
    past: number
    lastVisit: Date | null
  }
  /**
   * Nearest first around now: the next `CUSTOMER_SCHEDULE_LIMIT`, then the most recent `pastLimit` past ones. More
   * past entries exist while fewer than `counts.past` are listed.
   */
  items: CustomerScheduleItem[]
}
